import React from "react";
import { createPortal } from "react-dom";
import { Minus, Plus, Trash2, X } from "lucide-react";
import CustomDatePicker from "../../components/CustomDatePicker";
import { FINANCE_CATEGORIES, getFinanceCategoryColor } from "./financeConstants";

export default function FinanceEditModal({
  isOpen,
  isMobile,
  modalPos,
  isDraggingRef,
  handleHeaderMouseDown,
  onClose,
  onTouchStart,
  onTouchMove,
  onTouchEndClose,
  editingItem,
  theme,
  handleSubmit,
  formData,
  setFormData,
  handleDelete,
}) {
  if (!isOpen) return null;

  return createPortal(
    <div className={`fixed inset-0 z-[9999] flex ${isMobile ? "items-end" : "items-center justify-center p-4"}`} style={{ zIndex: 9999 }}>
      <div className="absolute inset-0 bg-transparent transition-opacity" />
      <div
        className={`
          bg-white w-full shadow-2xl border border-white/50 relative z-10 flex flex-col
          ${isMobile ? "rounded-t-[2.5rem] max-h-[90vh]" : "rounded-[2.5rem] max-w-md max-h-[85vh] rounded-b-[2.5rem]"}
        `}
        style={{
          transform: isMobile ? undefined : `translate(${modalPos.x}px, ${modalPos.y}px)`,
          transition: isDraggingRef.current ? "none" : "transform 0.1s ease-out",
        }}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEndClose}
      >
        {isMobile && (
          <div className="flex justify-center pt-3 pb-1">
            <div className="w-10 h-1 rounded-full bg-gray-300"></div>
          </div>
        )}

        <div
          className={`flex justify-between items-center flex-shrink-0 ${!isMobile ? "cursor-grab active:cursor-grabbing border-b-2 border-gray-100" : ""} ${isMobile ? "px-6 pt-2 pb-2" : "p-8 pb-4"}`}
          onMouseDown={handleHeaderMouseDown}
        >
          <h3 className={`text-2xl font-black ${theme.text} font-do-hyeon pointer-events-none select-none`}>
            {editingItem ? "내역 수정" : "새 내역 등록"}
          </h3>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="text-gray-400 hover:text-gray-600 bg-gray-100 p-2 rounded-full cursor-pointer"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <X size={20} />
          </button>
        </div>

        <div className={`overflow-y-auto custom-scrollbar ${isMobile ? "p-6 pt-0 pb-32" : "p-8 pt-2"}`}>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="flex gap-2 p-1 bg-gray-100/50 rounded-2xl border border-gray-100">
              {["수입", "지출"].map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setFormData({ ...formData, type, category: "식비" })}
                  className={`flex-1 py-3 rounded-xl text-sm font-bold transition ${formData.type === type
                    ? type === "수입"
                      ? "bg-blue-500 text-white shadow-md"
                      : "bg-rose-500 text-white shadow-md"
                    : "text-gray-400 hover:bg-white"
                    }`}
                >
                  {type}
                </button>
              ))}
            </div>

            <div>
              <label className="text-xs font-bold text-gray-500 mb-1.5 block ml-1">금액</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">₩</span>
                <input
                  type="text"
                  inputMode="numeric"
                  className={`w-full pl-10 pr-4 py-4 bg-[#F9F9F8] border-2 border-[#EAEAEA] rounded-2xl font-black text-xl focus:bg-white focus:${theme.border} outline-none transition ${theme.text}`}
                  placeholder="0"
                  value={formData.amount}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9]/g, "");
                    setFormData({ ...formData, amount: val ? Number(val).toLocaleString() : "" });
                  }}
                  required
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
                  <button
                    type="button"
                    onClick={() => {
                      const current = parseInt(formData.amount.replace(/,/g, "") || 0, 10);
                      const next = Math.max(0, current - 10000);
                      setFormData({ ...formData, amount: next.toLocaleString() });
                    }}
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-200 text-gray-500 hover:bg-gray-300 font-bold active:scale-95 transition"
                  >
                    <Minus size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const current = parseInt(formData.amount.replace(/,/g, "") || 0, 10);
                      const next = current + 10000;
                      setFormData({ ...formData, amount: next.toLocaleString() });
                    }}
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#354F38]/10 text-[#354F38] hover:bg-[#354F38]/20 font-bold active:scale-95 transition"
                  >
                    <Plus size={14} />
                  </button>
                </div>
              </div>
            </div>

            <div>
              <label className="text-xs font-bold text-gray-500 mb-1.5 block ml-1">날짜</label>
              <CustomDatePicker theme={theme} value={formData.date} onChange={(date) => setFormData({ ...formData, date })} className="w-full" />
            </div>

            <div>
              <label className="text-xs font-bold text-gray-500 mb-1.5 block ml-1">유형</label>
              <div className="flex flex-wrap gap-2">
                {FINANCE_CATEGORIES.map((cat) => {
                  const activeClass = getFinanceCategoryColor(cat);
                  return (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setFormData({ ...formData, category: cat })}
                      className={`px-3 py-2 rounded-xl text-xs font-bold border transition ${formData.category === cat ? `${activeClass} text-white border-transparent shadow-md` : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"}`}
                    >
                      {cat}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="text-xs font-bold text-gray-500 mb-1.5 block ml-1">내용</label>
              <textarea
                className={`w-full border-2 border-[#EAEAEA] p-3.5 rounded-2xl bg-[#F9F9F8] focus:bg-white focus:${theme.border} outline-none transition font-bold text-gray-700 resize-none`}
                rows="3"
                placeholder="예: 주일 간식 구매 "
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
              />
            </div>

            <div className="flex gap-2 mt-4 pt-2">
              <button type="submit" className={`flex-1 ${theme.bg} text-white py-4 rounded-2xl font-black hover:opacity-90 transition shadow-xl ${theme.shadow} text-lg`}>
                {editingItem ? "수정 완료" : "저장하기"}
              </button>
              {editingItem && (
                <button type="button" onClick={() => handleDelete(editingItem.id)} className="px-4 bg-red-50 text-red-500 rounded-2xl font-bold hover:bg-red-100 transition">
                  <Trash2 size={20} />
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
}
